import { z } from 'zod'

/**
 * Monthly client billing review state (invoicing-ops Phase 4, settled
 * decision 7: reviewer-light). One row per org+entity+period carrying a
 * status, an assignee, and an optional due date; no checklist engine and no
 * new InvoiceStatus values. Status is a VarChar(20) column backed by these
 * constants, matching the invoice-approval precedent.
 */
export const CLIENT_BILLING_REVIEW_STATUSES = {
  NOT_STARTED: 'NOT_STARTED',
  IN_REVIEW: 'IN_REVIEW',
  BLOCKED: 'BLOCKED',
  COMPLETE: 'COMPLETE',
} as const

export type ClientBillingReviewStatus =
  (typeof CLIENT_BILLING_REVIEW_STATUSES)[keyof typeof CLIENT_BILLING_REVIEW_STATUSES]

export const CLIENT_BILLING_REVIEW_STATUS_VALUES = Object.values(
  CLIENT_BILLING_REVIEW_STATUSES,
) as ClientBillingReviewStatus[]

export interface ClientBillingReview {
  id: string
  organizationId: string
  entityId: string
  entityName?: string | null
  /** First of the month, ISO date string (YYYY-MM-DD). */
  periodStart: string
  status: ClientBillingReviewStatus
  reviewerUserId: string | null
  reviewerName?: string | null
  /** ISO date string (YYYY-MM-DD) or null. */
  dueDate: string | null
  overdueNotifiedAt: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Upsert input: row identity is the natural key (entityId + period within the
 * caller's org), so the same payload creates or updates. period accepts
 * YYYY-MM or YYYY-MM-DD and is normalized server-side to the first of the
 * month (the parsePeriodParam convention).
 */
export const UpsertClientBillingReviewSchema = z.object({
  entityId: z.string().uuid(),
  period: z
    .string()
    .regex(/^\d{4}-\d{2}(-\d{2})?$/, 'period must be YYYY-MM or YYYY-MM-DD'),
  status: z
    .enum(
      CLIENT_BILLING_REVIEW_STATUS_VALUES as [
        ClientBillingReviewStatus,
        ...ClientBillingReviewStatus[],
      ],
    )
    .optional(),
  reviewerUserId: z.string().uuid().optional().nullable(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'dueDate must be YYYY-MM-DD')
    .optional()
    .nullable(),
})

export type UpsertClientBillingReviewInput = z.infer<
  typeof UpsertClientBillingReviewSchema
>
